import {
  AssignmentOutlined,
  AttachFileOutlined,
  CheckCircleOutline,
  DoNotDisturbAltOutlined,
  FileUploadOutlined,
  RadioButtonUnchecked,
  RemoveCircleOutlineOutlined,
} from "@mui/icons-material";
import { Box, Button, Tooltip } from "@mui/material";
import { deepOrange, green, grey, red } from "@mui/material/colors";
import { isArray, isEmpty, isNil, size } from "lodash";
import { useCallback, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import useAssociationUtils from "../../../../../../components/associations/hooks/useAssociationUtils";

import Flx from "../../../../../../components/layout/Flx";
import Txt from "../../../../../../components/typography/Txt";
import { useLoanFilesHook } from "../../../../../../hooks/useLoanFilesHook";
import { loanDrilldownSet } from "../../../../../../redux/actions/loanDrilldownActions";

import ObjectInfoTooltip from "../../../../../../components/data/ObjectInfoTooltip";
import UploadLoanFileModal from "../../../../../../components/modals/common/UploadLoanFileModal";
import { sidebarSetValues } from "../../../../../../redux/actions/sidebarActions";
import LoanBorrowerAssignFilesModal from "../LoanBorrowerAssignFilesModal";

const BorrowerRequiredDocumentsChecklist = ({ borrower }) => {
  const { getAssociationDocumentsByBorrowerId } = useAssociationUtils();
  const loanDrilldown = useSelector((state) => state.loanDrilldown);
  const borrowerFiles = useMemo(() => {
    return getAssociationDocumentsByBorrowerId(loanDrilldown, borrower?._id);
  }, [loanDrilldown, borrower]);

  // return <BorrowerRequiredDocumentsChecklistTable borrower={borrower} />;
  return (
    <Flx
      column
      gap={0}
      sx={{
        border: "1px solid #eeeeee",
        borderRadius: 2,
        // p: 1,
        ".required-doc-type-item": {
          "&:last-of-type": { borderBottom: "none" },
        },
      }}
    >
      <RequiredBorrowerDocTypeList
        borrower={borrower}
        borrowerId={borrower?._id}
        files={borrowerFiles}
        docGroup={"Borrower Documents"}
        docType={"Credit Report"}
      />
      <RequiredBorrowerDocTypeList
        borrower={borrower}
        borrowerId={borrower?._id}
        files={borrowerFiles}
        docGroup={"Borrower Documents"}
        docType={"Credit Authorization"}
      />
      <RequiredBorrowerDocTypeList
        borrower={borrower}
        borrowerId={borrower?._id}
        files={borrowerFiles}
        docGroup={"Borrower Documents"}
        docType={"Background Report"}
      />
      <RequiredBorrowerDocTypeList
        borrower={borrower}
        borrowerId={borrower?._id}
        files={borrowerFiles}
        docGroup={"Borrower Documents"}
        docType={"Loan Application"}
      />
      <RequiredBorrowerDocTypeList
        borrower={borrower}
        borrowerId={borrower?._id}
        files={borrowerFiles}
        docGroup={"Borrower Documents"}
        docType={"Photo Identification"}
      />
    </Flx>
  );
};

const RequiredBorrowerDocTypeList = ({
  files,
  borrower,
  docGroup,
  docType,
  borrowerId,
}) => {
  const [adding, setAdding] = useState(null);
  const filteredFiles = files?.filter((d) => d?.docType === docType) || [];
  //
  const loanDrilldown = useSelector((state) => state.loanDrilldown);
  const { loading, associateDocumentWithObjects } = useLoanFilesHook();
  const dispatch = useDispatch();
  //
  const handleAssignDocuments = useCallback(
    (updatedLoanData, newlyUploadedDocuments) => {
      if (isArray(updatedLoanData?.loanDocuments)) {
        const filteredNewFileIds = updatedLoanData?.loanDocuments
          ?.slice(0, size(newlyUploadedDocuments))
          ?.map((s) => s._id);

        associateDocumentWithObjects({
          dealId: loanDrilldown?._id,
          loanDocumentsIds: filteredNewFileIds,
          borrowerIds: [borrowerId],

          onSuccessFn: (d) => {
            dispatch(loanDrilldownSet(d));
            setAdding(false);
          },
        });
      }
    },
    [loanDrilldown?._id, associateDocumentWithObjects]
  );

  if (isEmpty(filteredFiles)) {
    return (
      <>
        <RequiredBorrowerDocTypeItem
          borrower={borrower}
          onClick={() => setAdding(true)}
          docType={docType}
        />
        {adding && (
          <UploadLoanFileModal
            show={adding}
            onClose={setAdding}
            docGroup={docGroup}
            loading={loading}
            onFileUploadSuccess={handleAssignDocuments}
            docType={docType}
          />
        )}
      </>
    );
  }
  return filteredFiles?.map((file) => (
    <RequiredBorrowerDocTypeItem
      key={file._id}
      file={file}
      borrower={borrower}
    />
  ));
};

const RequiredBorrowerDocTypeItem = ({ borrower, file, docType, onClick }) => {
  const STATUS_MAP = useMemo(() => {
    return {
      "New Upload": {
        color: grey[600],
        icon: <RadioButtonUnchecked className="thin-9" />,
        // icon: <DownloadingOutlined className="thin-9" />,
      },
      "Pending Review": {
        color: deepOrange[900],
        icon: <RadioButtonUnchecked className="thin-9" />,
        // icon: <DownloadingOutlined className="thin-9" />,
      },
      Accepted: {
        color: green[800],
        icon: <CheckCircleOutline className="thin-9" />,
      },
      Rejected: {
        color: red[800],
        icon: <DoNotDisturbAltOutlined className="thin-9" />,
      },
      Incomplete: {
        color: red[800],
        icon: <DoNotDisturbAltOutlined className="thin-9" />,
      },
    };
  }, []);

  const statusLabel = useMemo(() => {
    const status = file?.documentStatus;
    if (isNil(file?.documentStatus)) {
      return "Missing";
    }
    if (status === "New Upload") {
      return "Uploaded - Not Reviewed";
    }
    if (status === "Pending Review") {
      return "Pending Review";
    }
    if (status === "Rejected" || status === "Incomplete") {
      return status;
    }
    return null;
  }, [file?.documentStatus]);

  return (
    <Flx
      jw
      jb
      wrap
      ac
      className={"required-doc-type-item"}
      sx={{
        borderBottom: "1px solid #eeeeee",
        borderRadius: "4px",
        py: 1,
        px: 1.4,
      }}
      grow={1}
      g={0.5}
    >
      <Flx ac g={0.3}>
        <Txt
          sx={{
            display: "flex",
            alignItems: "center",
            gap: "4px",
            fontSize: "11px",
            fontWeight: 400,
            color: STATUS_MAP[file?.documentStatus]?.color || grey[600],
            svg: {
              mr: 0.4,
              fontSize: "16px",
            },
          }}
        >
          {STATUS_MAP[file?.documentStatus]?.icon || (
            <RemoveCircleOutlineOutlined className="thin-9" />
          )}
          {file?.docType || docType}
        </Txt>
        {/* {file ? `Uploaded: ${formatDate(file.uploadDate)}` : "Missing"} */}
        {statusLabel && (
          <>
            <Txt sx={{ alignSelf: "center" }} secondary>
              <span
                style={{
                  paddingLeft: "4px",
                  fontWeight: 400,
                  fontSize: "11px",
                  fontStyle: "italic",
                }}
              >
                ({statusLabel})
              </span>
            </Txt>
          </>
        )}
      </Flx>
      <Flx ac sx={{ ml: 2 }} wrap g={1}>
        {isNil(file) ? <AssignBorrowerFilesButton borrower={borrower} /> : null}
        <PreviewableLoanFileButton
          onClick={onClick}
          // maxWidth="unset"
          maxWidth="165px"
          // iconSize={18}
          sx={{
            fontStyle: isNil(file) ? "italic" : "normal",
            fontWeight: isNil(file) ? 400 : 400,
          }}
          text={isNil(file) ? "Upload New File" : file?.file_display_name}
          startIcon={
            isNil(file) ? (
              <FileUploadOutlined sx={{ mx: 0.5 }} />
            ) : (
              <AttachFileOutlined
                className="thin"
                sx={{ transform: "rotate(180deg)" }}
              />
            )
          }
          file={file}
        />
      </Flx>
    </Flx>
  );
};

const AssignBorrowerFilesButton = ({ borrower }) => {
  const [show, setShow] = useState(false);
  return (
    <>
      <PreviewableLoanFileButton
        onClick={() => setShow(true)}
        // maxWidth="unset"
        maxWidth="165px"
        // iconSize={18}
        sx={{
          // ml: 2,
          fontStyle: "italic",
          fontWeight: 400,
        }}
        text={"Assign Existing File"}
        startIcon={<AssignmentOutlined sx={{ mx: 0.5 }} className="thin" />}
      />

      <LoanBorrowerAssignFilesModal
        show={show}
        borrowerId={borrower?._id}
        onClose={() => setShow(false)}
      />
    </>
  );
};

const PreviewableLoanFileButton = ({
  text,
  // startIcon = (
  //   <VisibilityOutlined className="thin" sx={{ transform: "rotate(180deg)" }} />
  // ),
  startIcon = (
    <AttachFileOutlined className="thin" sx={{ transform: "rotate(180deg)" }} />
  ),
  file,
  onClick,
  iconSize = 14,
  fontSize = 11,
  maxWidth = "270px",
  sx,
}) => {
  const dispatch = useDispatch();

  const onPreviewClick = useCallback(() => {
    if (onClick) {
      onClick(file);
    }
    if (!file) {
      return;
    }
    dispatch(
      sidebarSetValues({
        type: "filePreview",
        state: file,
      })
    );
  }, [dispatch, file, onClick]);

  const styles = useMemo(() => {
    return {
      py: 0.5,
      px: 0.5,
      fontWeight: 400,
      border: "1px solid #e3f2fd",
      /* REQUIRED for ellipsis */
      maxWidth, // adjust as needed
      minWidth: 0,
      justifyContent: "flex-start",

      fontSize: "12px !important",

      ".MuiButton-startIcon": {
        mr: "1px",
        flexShrink: 0, // prevent icon from shrinking
      },

      ".MuiSvgIcon-root": {
        fontSize: `${iconSize}px !important`,
        // fontSize: "14px !important",
      },

      ...sx,
    };
  }, [sx, maxWidth, iconSize]);

  const buttonContent = (
    <Button
      onClick={onPreviewClick}
      variant="text"
      size="small"
      sx={styles}
      startIcon={startIcon}
    >
      <span
        style={{
          fontSize,
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
          display: "block",
          minWidth: 0,
          textAlign: "left",
          flexGrow: 1,
        }}
      >
        {text || file?.file_display_name}
      </span>
    </Button>
  );

  if (isNil(file)) {
    return (
      <Tooltip
        title={
          <>
            No File Uploaded. <b>Click to upload.</b>
          </>
        }
        placement="right"
      >
        <Box sx={{ flexGrow: 0, width: "max-content" }}>{buttonContent}</Box>
      </Tooltip>
    );
  }

  return (
    <ObjectInfoTooltip
      title={file?.file_display_name || "File Details"}
      placement="right"
      returnEmptyChildWhenMissingData
      format={[
        { field: "modifiedDate", type: "date" },
        { field: "uploadDate", type: "date" },
        { field: "reportEffectiveDate", type: "date" },
      ]}
      data={file}
      excludeKeys={[
        "_id",
        "content_type",
        "container_type",
        "dealId",
        "file_storage_name",
        "containerName",
      ]}
    >
      {buttonContent}
    </ObjectInfoTooltip>
  );
};
export default BorrowerRequiredDocumentsChecklist;
